import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Card, HStack, Separator, Spinner, Text, VStack, Wrap } from '@chakra-ui/react';
import { useBoardSeatCounts } from '@src/components/sco/Advisors/hooks/useBoardSeatCounts';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorMarketplaceActionButton } from '@src/components/sco/Advisors/shared/AdvisorMarketplaceActionButton';
import { ProfileField } from '@src/components/sco/Advisors/shared/ProfileField';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { StatBox } from '@src/components/sco/Advisors/shared/StatBox';
import { advisorMarketplaceActions } from '@src/components/sco/Advisors/utils/advisorMarketplaceActions';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useState } from 'react';
type AdvisorProfileCardProps = {
  userId: string;
  /** When the parent query already fetched advisorProfile, pass it to skip a round trip. */
  advisorProfile?: ScoAdvisorProfile | null;
  showActions?: boolean;
};
const ADVISOR_PROFILE_QUERY = gql`
  query AdvisorProfileCard($userId: String!) {
    advisorProfile(userId: $userId) {
      id
      advisorProfileId
      statusKey
      isApproved
      warmIntroKeys
      warmIntros {
        id
        key
        title
      }
      availabilityHoursPerMonth
      responsesCount
      foundersHelpedCount
      hourlyRate
      canAcceptSessions
      categories {
        id
        key
        title
      }
    }
  }
`;
export const AdvisorProfileCard = props => {
  const $ = _c(59);
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  const [inviteOpen, setInviteOpen] = useState(false);
  const {
    counts: seatCounts
  } = useBoardSeatCounts();
  const hasOpenSeats = seatCounts.drafts + seatCounts.searching > 0;
  const t0 = !!props.advisorProfile || !props.userId;
  let t1;
  if ($[0] !== props.userId) {
    t1 = {
      userId: props.userId
    };
    $[0] = props.userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      skip: t0,
      variables: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useQuery(ADVISOR_PROFILE_QUERY, t2);
  const profile = props.advisorProfile ?? query.data?.advisorProfile;
  if (!props.advisorProfile && query.loading) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Card.Root bg="colorPalette.container" borderColor="colorPalette.bg/50" variant="outline"><Card.Body><Spinner size="sm" /></Card.Body></Card.Root>;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (!profile?.isApproved) {
    return null;
  }
  let T0;
  let T1;
  let availabilityLabel;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] !== profile.availabilityHoursPerMonth || $[7] !== profile.foundersHelpedCount || $[8] !== profile.responsesCount || $[9] !== profile.warmIntros) {
    const warmIntroLabels = profile.warmIntros?.map(_temp).filter(Boolean) ?? [];
    availabilityLabel = profile.availabilityHoursPerMonth != null ? `${profile.availabilityHoursPerMonth} hrs / month` : null;
    T1 = Card.Root;
    t7 = "hidden";
    t8 = "outline";
    t9 = "full";
    T0 = Card.Body;
    t3 = 4;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <HStack justify="space-between"><Text textStyle="labelXs">Advisor</Text><Badge colorPalette="green" textStyle="label2xs" variant="subtle">● Open to advisory</Badge></HStack>;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    let t10;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Separator height="8" orientation="vertical" />;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const t11 = String(profile.responsesCount ?? 0);
    let t12;
    if ($[22] !== t11) {
      t12 = <StatBox label="Responses" value={t11} />;
      $[22] = t11;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    const t13 = String(profile.foundersHelpedCount ?? 0);
    let t14;
    if ($[24] !== t13) {
      t14 = <StatBox label="Startups helped" value={t13} />;
      $[24] = t13;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    if ($[26] !== t12 || $[27] !== t14) {
      t5 = <HStack border="1px solid" borderColor="colorPalette.border/40" borderRadius="lg" separator={t10}>{t12}{t14}</HStack>;
      $[26] = t12;
      $[27] = t14;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    t6 = warmIntroLabels.length > 0 && <ProfileField label="Makes warm intros"><Wrap gap={1.5}>{warmIntroLabels.map(_temp2)}</Wrap></ProfileField>;
    $[6] = profile.availabilityHoursPerMonth;
    $[7] = profile.foundersHelpedCount;
    $[8] = profile.responsesCount;
    $[9] = profile.warmIntros;
    $[10] = T0;
    $[11] = T1;
    $[12] = availabilityLabel;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    availabilityLabel = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[29] !== availabilityLabel) {
    t10 = availabilityLabel && <ProfileField label="Availability">{availabilityLabel}</ProfileField>;
    $[29] = availabilityLabel;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== currentUser?.userId || $[32] !== hasOpenSeats || $[33] !== paths || $[34] !== profile.canAcceptSessions || $[35] !== props.showActions || $[36] !== props.userId) {
    t11 = props.showActions !== false && <VStack align="stretch" gap={2}>{advisorMarketplaceActions({
        canAcceptSessions: profile.canAcceptSessions,
        hasOpenSeats,
        isSelf: props.userId === currentUser?.userId
      }).map(action => <AdvisorMarketplaceActionButton key={action.key} action={action} paths={paths} userId={props.userId} onInvite={() => setInviteOpen(true)} />)}</VStack>;
    $[31] = currentUser?.userId;
    $[32] = hasOpenSeats;
    $[33] = paths;
    $[34] = profile.canAcceptSessions;
    $[35] = props.showActions;
    $[36] = props.userId;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== T0 || $[39] !== t10 || $[40] !== t11 || $[41] !== t3 || $[42] !== t4 || $[43] !== t5 || $[44] !== t6) {
    t12 = <T0 gap={t3}>{t4}{t5}{t6}{t10}{t11}</T0>;
    $[38] = T0;
    $[39] = t10;
    $[40] = t11;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== T1 || $[47] !== t12 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9) {
    t13 = <T1 overflow={t7} variant={t8} width={t9}>{t12}</T1>;
    $[46] = T1;
    $[47] = t12;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setInviteOpen(false);
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== inviteOpen || $[54] !== props.userId) {
    t15 = <InviteToBoardModal advisorUserId={props.userId} isOpen={inviteOpen} onClose={t14} />;
    $[53] = inviteOpen;
    $[54] = props.userId;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== t13 || $[57] !== t15) {
    t16 = <>{t13}{t15}</>;
    $[56] = t13;
    $[57] = t15;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  return t16;
};
function _temp(w) {
  return w.title;
}
function _temp2(label) {
  return <SpecialtyTag key={label} colorPalette="cyan" label={label} />;
}